import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faChevronDown,
  faSpinner,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import './HMSPartnerSearch.css';

/**
 * HMSPartnerSearch
 *
 * Reusable search-as-you-type combobox for CRM partners. Selected partner
 * renders as a green "chip" with a clear button; an empty selection
 * renders as a search input that opens a dropdown of matching profiles.
 *
 * Modeled on the FuelEnquiry CustomerPicker - same UX vocabulary - but
 * generic and self-contained (own CSS, no enquiry-specific concepts like
 * "unlinked customer" or inline-create CTAs). Drop in anywhere a screen
 * needs to pick a single CRM partner.
 *
 * Props:
 *  - value:         partner object | null - currently selected profile
 *  - onChange:      (partner|null) => void - selection callback
 *  - placeholder:   string - search input placeholder
 *  - searchParams:  object - extra query params merged into the
 *                   /api/crm/quick-search-profiles/ request (e.g.
 *                   { fuels_debtors_only: 'true' }). Default {}.
 *  - disabled:      bool - lock the picker while a parent is saving
 *  - noResultsCopy: string - empty-state copy
 *  - autoFocus:     bool - focus the search input on mount/open
 */

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

// Small grey line under a partner so the user can confirm they picked the
// right entity. Same look as DeliveryPartnerSearch / RecapPartnerSearch.
const ADDRESS_STYLE = {
  fontSize: '0.8em',
  fontWeight: 400,
  color: '#888',
  marginTop: '2px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// The quick-search row carries a flat ``head_office_address``; the full CRM
// profile blob carries ``head_office_address_data`` instead.
const profileAddress = (p) => (
  p?.head_office_address
  || p?.head_office_address_data?.formatted_address
  || p?.head_office_address_data?.place_name
  || ''
);

const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
};

const HMSPartnerSearch = ({
  value,
  onChange,
  placeholder = 'Search partner by legal name…',
  searchParams = {},
  disabled = false,
  noResultsCopy = 'No matching partners. Try a different search term.',
  autoFocus = false,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const debouncedSearch = useDebouncedValue(searchInput, DEBOUNCE_MS);

  // Stable JSON snapshot of searchParams so the search effect doesn't
  // re-fire on every parent render when callers pass an object literal.
  const searchParamsKey = useMemo(
    () => JSON.stringify(searchParams || {}),
    [searchParams]
  );

  useEffect(() => {
    const handleDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, []);

  useEffect(() => {
    if (autoFocus && !value && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, value]);

  useEffect(() => {
    if (!isOpen) return;
    const reqId = ++requestIdRef.current;
    const term = (debouncedSearch || '').trim();

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          ...(searchParams || {}),
          page_size: PAGE_SIZE,
        };
        if (term) params.search = term;
        const response = await axiosInstance.get(
          '/api/crm/quick-search-profiles/',
          { params }
        );
        if (cancelled || reqId !== requestIdRef.current) return;
        const data = response.data;
        const rows = Array.isArray(data) ? data : (data?.results || []);
        setResults(rows);
      } catch (err) {
        if (cancelled || reqId !== requestIdRef.current) return;
        console.error('HMSPartnerSearch: search failed', err);
        setError(err?.response?.data?.detail || err?.message || 'Search failed.');
        setResults([]);
      } finally {
        if (!cancelled && reqId === requestIdRef.current) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isOpen, searchParamsKey]);

  const handlePick = (row) => {
    setIsOpen(false);
    setSearchInput('');
    onChange?.(row);
  };

  const handleClear = () => {
    onChange?.(null);
    setSearchInput('');
  };

  if (value) {
    return (
      <div className="hms-partner-search" ref={wrapperRef}>
        <div className="hms-partner-search-chip">
          <div className="hms-partner-search-chip-name">
            {value.legal_entity_name || value.trading_short_name || `#${value.id}`}
            {value.trading_short_name && value.trading_short_name !== value.legal_entity_name && (
              <span className="hms-partner-search-chip-trading">
                {' '}({value.trading_short_name})
              </span>
            )}
            {profileAddress(value) && (
              <div style={ADDRESS_STYLE}>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> {profileAddress(value)}
              </div>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              className="hms-partner-search-clear"
              onClick={handleClear}
              title="Clear selection"
              aria-label="Clear partner selection"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="hms-partner-search"
      ref={wrapperRef}
      style={{ zIndex: isOpen ? 1001 : 1 }}
    >
      <div className={`hms-partner-search-control ${isOpen ? 'is-open' : ''}`}>
        <FontAwesomeIcon icon={faSearch} className="hms-partner-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="hms-partner-search-input"
          placeholder={placeholder}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onFocus={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          autoComplete="off"
        />
        <FontAwesomeIcon icon={faChevronDown} className="hms-partner-search-chevron" />
      </div>

      {isOpen && (
        <div className="hms-partner-search-dropdown" role="listbox">
          {loading && (
            <div className="hms-partner-search-state">
              <FontAwesomeIcon icon={faSpinner} spin /> Searching…
            </div>
          )}
          {!loading && error && (
            <div className="hms-partner-search-state hms-partner-search-state-error">
              {error}
            </div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="hms-partner-search-state">{noResultsCopy}</div>
          )}
          {/* The picker asks for the top PAGE_SIZE matches only. With 600+
              partners on the system, a full list is the one case where the
              partner you want may not be on screen — and a list that stops
              silently is indistinguishable from one with nothing more in it.
              Say so, and say what to do about it. */}
          {!loading && !error && results.length >= PAGE_SIZE && (
            <div className="hms-partner-search-state">
              Showing the first {PAGE_SIZE} matches — keep typing to narrow it down.
            </div>
          )}
          {!loading && !error && results.map((row) => {
            const showTrading = !!(
              row.trading_short_name
              && row.trading_short_name !== row.legal_entity_name
            );
            const hasMeta = !!row.monday_account_number
              || (Array.isArray(row.creditor_debtor_selections)
                && row.creditor_debtor_selections.length > 0);
            return (
              <button
                key={row.id}
                type="button"
                role="option"
                className="hms-partner-search-option"
                onClick={() => handlePick(row)}
              >
                <div className="hms-partner-search-option-name">
                  {row.legal_entity_name || row.trading_short_name || `#${row.id}`}
                </div>
                {showTrading && (
                  <div className="hms-partner-search-option-trading">
                    Trading as: {row.trading_short_name}
                  </div>
                )}
                {row.head_office_address && (
                  <div style={ADDRESS_STYLE}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> {row.head_office_address}
                  </div>
                )}
                {hasMeta && (
                  <div className="hms-partner-search-option-meta">
                    {row.monday_account_number && (
                      <span className="hms-partner-search-option-meta-item">
                        Acct {row.monday_account_number}
                      </span>
                    )}
                    {Array.isArray(row.creditor_debtor_selections)
                      && row.creditor_debtor_selections.length > 0 && (
                      <span className="hms-partner-search-option-meta-item">
                        {row.creditor_debtor_selections.join(' / ')}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HMSPartnerSearch;
